import { z } from "zod";

import { taskMetadataBaseSchema } from "../../taskMetadata";

const executionPromptContextSubtaskSchema = z.object({
  dependencies: z.array(z.string()),
  description: z.string(),
  id: z.string(),
  implementation_details: z.string().nullable(),
  intent: z.string().nullable(),
  issue_key: z.string().nullable(),
  reference_repository_slugs: z.array(z.string()),
  repository_slug: z.string().nullable(),
  self_assessed_risk: z.enum(["low", "medium", "high"]).nullable(),
  size_symbol: z.string().nullable(),
  technical_constraints: z.array(z.string()),
  title: z.string(),
  uow_ids: z.array(z.string()),
});

const executionPromptContextUnitOfWorkSchema = z.object({
  acceptance_criteria: z.array(z.string()),
  description: z.string(),
  estimated_effort: z.string().nullable(),
  primary_repository_slug: z.string(),
  reference_repository_slugs: z.array(z.string()),
  technical_notes: z.array(z.string()),
  title: z.string(),
  unit_of_work_id: z.string(),
});

const executionPromptPlanningContextSchema = z.object({
  integration_notes: z.array(z.string()),
  overview: z.string().nullable(),
  plan_complexity_score: z.enum(["low", "medium", "high"]).nullable(),
  prerequisites: z.array(z.string()),
  trace_id: z.string().nullable(),
});

const executionPromptWorkspaceStateSchema = z.object({
  primary_repository_slug: z.string().nullable(),
  selected_base_shas: z.record(z.string(), z.string()),
  selected_branches: z.record(z.string(), z.string()),
  selected_repos: z.array(z.string()),
  snapshot: z.unknown().nullable(),
  touchpoints: z.unknown().nullable(),
});

const executionPromptOutputContractSchema = z.object({
  required_fields: z.array(z.string()),
  schema: z.string(),
});

export const executionPromptContextSchema = z.object({
  output_contract: executionPromptOutputContractSchema,
  plan_step_id: z.string().nullable(),
  planning_context: executionPromptPlanningContextSchema,
  security_mandate: z.string(),
  subtask: executionPromptContextSubtaskSchema.nullable(),
  system_role: z.string(),
  units_of_work: z.array(executionPromptContextUnitOfWorkSchema).nullable(),
  workspace_state: executionPromptWorkspaceStateSchema,
});

export type ExecutionPromptContextParsed = z.infer<
  typeof executionPromptContextSchema
>;

/**
 * Metadata schema for `execution.setup.v1` tasks.
 */
export const executionSetupMetadataSchema = taskMetadataBaseSchema
  .extend({
    execution_prompt_context: executionPromptContextSchema,
    flow: z.object({ kind: z.literal("execution.setup.v1") }).passthrough(),
  })
  .passthrough();

export type ExecutionSetupMetadata = z.infer<
  typeof executionSetupMetadataSchema
>;

/**
 * Metadata schema for `execution.implement.v1` tasks.
 *
 * Extends the base with optional review guideline fields.
 */
export const executionImplementMetadataSchema = taskMetadataBaseSchema
  .extend({
    flow: z.object({ kind: z.literal("execution.implement.v1") }).passthrough(),
    repository_review_guideline_functional: z.string().optional(),
    repository_review_guideline_ui: z.string().optional(),
  })
  .passthrough();

export type ExecutionImplementMetadata = z.infer<
  typeof executionImplementMetadataSchema
>;
